import { format, parseISO } from 'date-fns';

import { zonedDateString, zonedEvent } from '@/lib/timezone';
import { findResolvedShift } from './helpers';
import type { IEvent } from './interfaces';
import type { TResolvedShift, TWorkingHours } from './types';

/** Fit the opening day in a phone's week grid, with room for the closing label. */
export function getCompactWeekGrid({
  days,
  staffId,
  workingHours,
  resolvedShifts,
  events,
  timeZone,
  viewportHeight,
}: {
  days: Date[];
  staffId: string | undefined;
  workingHours: TWorkingHours;
  resolvedShifts: TResolvedShift[];
  events: IEvent[];
  timeZone: string;
  viewportHeight: number;
}) {
  const starts: number[] = [];
  const ends: number[] = [];
  const dates = new Set(days.map((day) => format(day, 'yyyy-MM-dd')));
  for (const day of days) {
    const shift = findResolvedShift(resolvedShifts, staffId, day);
    if (shift) {
      if (!shift.isOff) {
        for (const interval of shift.intervals) {
          starts.push(interval.startMinutes / 60);
          ends.push(interval.endMinutes / 60);
        }
      }
    } else {
      const hours = workingHours[day.getDay()];
      if (hours && hours.to > hours.from) {
        starts.push(hours.from);
        ends.push(hours.to);
      }
    }
  }
  for (const event of events) {
    if (!dates.has(zonedDateString(parseISO(event.startDate), timeZone)))
      continue;
    const start = zonedEvent(event.startDate, timeZone);
    const end = zonedEvent(event.endDate, timeZone);
    starts.push(start.getHours() + start.getMinutes() / 60);
    ends.push(
      zonedDateString(parseISO(event.endDate), timeZone) >
        zonedDateString(parseISO(event.startDate), timeZone)
        ? 24
        : end.getHours() + end.getMinutes() / 60
    );
  }
  const firstHour = Math.floor(starts.length ? Math.min(...starts) : 9);
  const lastHour = Math.ceil(ends.length ? Math.max(...ends) : 18);
  // Below 28px/hour even the hour labels become hard to read. Keep scrolling
  // for very long days or short landscape screens rather than hiding bookings.
  const hourPx = viewportHeight
    ? Math.max(
        28,
        Math.min(44, (viewportHeight - 80) / (lastHour - firstHour + 0.5))
      )
    : 44;
  return { firstHour, hourPx };
}
